import { api, ApiError } from '../api'

/**
 * The answer outbox (offline-first sessions, Sam, 9 Oct 2026).
 *
 * The browser marks each answer itself and shows the result at once. The
 * answer is put here, kept in the browser's storage, and sent to the server
 * in the background, oldest first. A lost connection only means it waits.
 *
 * Sending twice is safe: each answer carries its own id
 * (`client_answer_id`), and the server keeps the first and replays it for
 * any repeat. So when a reply is lost on a bad connection, sending again
 * cannot make a second trial.
 *
 * What stops the queue, and what happens:
 *   no connection, server error, Word Sprout asleep  wait, try again later
 *   the child's PIN ticket has run out               wait until they unlock
 *   the server refuses the answer itself (400, 404)  set it aside, carry on
 *   another family is signed in on this browser      wait for its own family
 *
 * How it works, and why: research/how-it-works.md, section 5.
 */

export interface Pending {
  id: string
  sessionId: string
  learnerId: string
  body: Record<string, unknown>
  /** The family signed in when it was answered. Sent only while that family is. */
  familyId?: string
}

const KEY = 'ws-outbox-v1'
// Answers the server refused. Kept, not deleted, so they can still be
// recovered if the refusal turns out to be a mistake (Codex round 20, finding 4).
const HELD_KEY = 'ws-outbox-held-v1'
const FAMILY_KEY = 'ws-outbox-family'
const RETRY_MS = 20_000

// Who is signed in, remembered so a reload while offline still knows.
let family: string | null = (() => {
  try { return window.localStorage.getItem(FAMILY_KEY) } catch { return null }
})()

/**
 * Codex round 20, finding 4: family A's answers waited (signed out, or the
 * sign-in ran out), family B signed in on the same browser, and the outbox
 * sent A's answers with B's sign-in. The server rightly said "no such
 * session", and the outbox deleted them as refused for good. So an answer
 * carries its family, waits while another one is signed in, and a refusal
 * sets it aside instead of deleting it.
 */
function ours(p: Pending): boolean {
  return !p.familyId || p.familyId === family
}

function setAside(p: Pending): void {
  try {
    const raw = window.localStorage.getItem(HELD_KEY)
    const held = raw ? (JSON.parse(raw) as Pending[]) : []
    window.localStorage.setItem(HELD_KEY, JSON.stringify([...held, p].slice(-500)))
  } catch {
    // Storage refused: nothing more can be kept.
  }
}

let memory: Pending[] | null = null // used when storage is blocked
const listeners = new Set<() => void>()
let flushing: Promise<void> | null = null
let timer: number | null = null

function read(): Pending[] {
  if (memory) return memory
  try {
    const raw = window.localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as Pending[]) : []
  } catch {
    memory = []
    return memory
  }
}

function write(list: Pending[]): void {
  if (memory) {
    memory = list
  } else {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list))
    } catch {
      // Storage refused (private window, full disk): keep going in memory,
      // so the answers still go when the connection allows.
      memory = list
    }
  }
  listeners.forEach((fn) => fn())
}

function later(): void {
  if (timer !== null || typeof window === 'undefined') return
  timer = window.setTimeout(() => {
    timer = null
    void outbox.flush()
  }, RETRY_MS)
}

export const outbox = {
  add(item: Omit<Pending, 'id'> & { id?: string }): string {
    const id = item.id ?? newId()
    write([...read(), {
      ...item, id, familyId: item.familyId ?? family ?? undefined,
      body: { ...item.body, client_answer_id: id },
    }])
    void outbox.flush()
    return id
  },

  /**
   * Are waiting answers kept in the browser's storage, so they survive a
   * reload or a closed tab? False once storage has refused them and they are
   * only in memory (Codex round 17, finding 2): then the screen must not say
   * they are safe.
   */
  durable(): boolean {
    read()
    return memory === null
  },

  /** Answers not yet confirmed by the server, for one session or all. */
  count(sessionId?: string): number {
    return read().filter((p) => !sessionId || p.sessionId === sessionId).length
  },

  /**
   * Who is signed in now (from /api/state), or null when nobody is. Answers
   * of another family wait until it signs back in. Then send what is ours.
   */
  setFamily(id: string | null): void {
    family = id
    try {
      if (id) window.localStorage.setItem(FAMILY_KEY, id)
      else window.localStorage.removeItem(FAMILY_KEY)
    } catch {
      // Storage refused: remembered for this page only.
    }
    void outbox.flush()
  },

  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => { listeners.delete(fn) }
  },

  /** Send what is waiting, oldest first. Safe to call any time, as often as you like. */
  flush(): Promise<void> {
    if (flushing) return flushing
    const run = async (): Promise<void> => {
      // A child whose answer needs a PIN that is not held right now. Their
      // answers wait, in order; everyone else's still go (audit, 12 Oct
      // 2026: one boy's locked answer at the front held back his brother's).
      const waiting = new Set<string>()
      for (;;) {
        const next = read().find((p) => !waiting.has(p.learnerId) && ours(p))
        if (!next) {
          if (waiting.size) later()
          return
        }
        try {
          await api.sendAnswer(next.sessionId, next.learnerId, next.body)
        } catch (err) {
          const kind = err instanceof ApiError ? err.kind : 'offline'
          if (kind === 'request') {
            // The server will not accept this one (the session is gone,
            // say). Keeping it in the queue would block every answer behind
            // it, so it is set aside, not deleted.
            // The id only, not the answer itself (Codex round 22, finding 6).
            console.warn('Word Sprout: an answer was refused and set aside', next.id, err)
            setAside(next)
          } else if (kind === 'locked' || kind === 'auth') {
            waiting.add(next.learnerId)
            continue
          } else {
            later()
            return
          }
        }
        write(read().filter((p) => p.id !== next.id))
      }
    }
    // Cleared in `.finally`, which always runs after this assignment. Clearing
    // it inside `run` broke on an empty queue: `run` finished before the
    // assignment, so the flag was set after being cleared and stayed set,
    // and nothing was ever sent again (found in the end-to-end test).
    flushing = run().finally(() => { flushing = null })
    return flushing
  },
}

function newId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

// Try again the moment the connection comes back, and once on load for
// anything left from last time.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { void outbox.flush() })
  window.setTimeout(() => { void outbox.flush() }, 2_000)
}
